import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable } from 'rxjs';
import { 
  faChevronLeft, 
  faFileAlt, 
  faCheck, 
  faCircleExclamation 
} from '@fortawesome/free-solid-svg-icons';
import { IDocumentResponse } from 'src/app/models/document.model';
import { IFormFieldsRequest } from 'src/app/models/form.interface';
import { DocumentsService } from 'src/app/services/documents.service';
import { ModulesService } from 'src/app/services/modules.service';
import { TemplatesService } from 'src/app/services/templates.service';

@Component({
  selector: 'app-generate-docs-v2',
  templateUrl: './generate-docs.component.html',
  styleUrls: ['./generate-docs.component.sass']
})
export class GenerateDocsV2Component implements OnInit {
  // Icons
  faChevronLeft = faChevronLeft;
  faFileAlt = faFileAlt;
  faCheck = faCheck;
  faCircleExclamation = faCircleExclamation;

  // State
  loading = false;
  anamneseId: string | null = '';
  patientId: string | null = '';
  isModelPatient = false;
  private targetModuleId = '';

  // Documents
  tcleFiles: IDocumentResponse[] = [];
  requiredDocs: IDocumentResponse[] = [];
  optionalDocs: IDocumentResponse[] = [];
  selectedTcles: string[] = [];
  finalDocsList: IDocumentResponse[] = [];

  // Module
  viewModules$: Observable<{ moduleName: string; moduleNamePascalCase: string; moduleNameInSingular: string; }>;

  constructor(
    private documentService: DocumentsService,
    private templatesService: TemplatesService,
    private router: Router,
    private route: ActivatedRoute,
    private modulesService: ModulesService
  ) {
    this.viewModules$ = combineLatest({
      moduleName: this.modulesService.moduleName$,
      moduleNamePascalCase: this.modulesService.moduleNamePascalCase$,
      moduleNameInSingular: this.modulesService.moduleNameInSingular$
    });
  }

  ngOnInit(): void {
    this.loading = true;

    // Get anamneseId from route params
    this.route.paramMap.subscribe(params => {
      this.anamneseId = params.get('anamneseId');
    });

    // Get patientId from query params
    this.patientId = this.route.snapshot.queryParams['patientId'] || '';
    const qpIsModel = this.route.snapshot.queryParams['isModelPatient'];
    this.isModelPatient = qpIsModel === true || qpIsModel === 'true';

    this.resolveModuleId()
      .then((moduleId) => {
        this.targetModuleId = moduleId || this.modulesService.moduleId;
        return this.templatesService.list(['ANAMNESE_GROUP', 'TCLE'], this.targetModuleId, this.isModelPatient);
      })
      .then((response) => {
        // Required docs
        const baseRequired = response.filter(doc => 
          doc.tags?.some(tag => tag.description === 'ANAMNESE_REQUIRED' || tag.description === 'ANAMNESE_VIRTUAL')
        );
        const contractDocs = response.filter(doc => 
          doc.tags?.some(tag => tag.description === 'PRESTACAO_DE_SERVICO' || tag.description === 'PRESTACAO_DE_SERVICO_MODELO')
        );
        const seen = new Set<string>();
        this.requiredDocs = [...baseRequired, ...contractDocs].filter(d => 
          seen.has(d.id) ? false : (seen.add(d.id), true)
        );

        // Optional docs
        this.optionalDocs = response.filter(doc => 
          doc.tags?.some(tag => tag.description === 'ANAMNESE_OPTIONAL')
        );

        // TCLE files
        this.tcleFiles = response.filter(doc => 
          doc.tags?.some(tag => tag.description === 'TCLE')
        );
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private resolveModuleId(): Promise<string> {
    const moduleIdFromQP = this.route.snapshot.queryParams['moduleId'] || '';
    if (moduleIdFromQP) return Promise.resolve(moduleIdFromQP);

    if ((this.modulesService.moduleName || '').toUpperCase() === 'PACIENTES') {
      return Promise.resolve(this.modulesService.moduleId);
    }

    return new Promise((resolve) => {
      this.modulesService.listModules().subscribe({
        next: (mods) => {
          const pacientes = (mods || []).find(m => (m.description || '').toUpperCase() === 'PACIENTES');
          resolve(pacientes?.id || this.modulesService.moduleId);
        },
        error: () => resolve(this.modulesService.moduleId)
      });
    });
  }

  goBack(): void {
    if (this.patientId) {
      this.router.navigate(['main', 'v2', 'patients', this.patientId, 'edit']);
    } else {
      this.router.navigate(['main', 'v2', 'patients']);
    }
  }

  toggleTcle(id: string): void {
    const index = this.selectedTcles.indexOf(id);
    if (index > -1) {
      this.selectedTcles.splice(index, 1);
    } else {
      this.selectedTcles.push(id);
    }
  }

  isTcleSelected(id: string): boolean {
    return this.selectedTcles.includes(id);
  }

  onChangeOptionalDoc(index: number, include: boolean): void {
    const doc = this.optionalDocs[index];
    if (include) {
      if (!this.finalDocsList.some(d => d.id === doc.id)) {
        this.finalDocsList.push(doc);
      }
    } else {
      this.finalDocsList = this.finalDocsList.filter(d => d.id !== doc.id);
    }
  }

  getOptionalDocValue(id: string): boolean {
    return this.finalDocsList.some(doc => doc.id === id);
  }

  isFormValid(): boolean {
    return this.tcleFiles.length === 0 || this.selectedTcles.length > 0;
  }

  generateDocs(): void {
    if (!this.isFormValid()) {
      return;
    }

    this.loading = true;

    const selectedTclesDocs = this.tcleFiles.filter(doc => this.selectedTcles.includes(doc.id));
    const allDocs = [...this.finalDocsList, ...this.requiredDocs, ...selectedTclesDocs];

    const request: IFormFieldsRequest = {
      document_ids: allDocs.map(doc => doc.id),
    };

    this.router.navigate(['main', 'v2', 'patients', 'document-form'], {
      queryParams: {
        request: btoa(JSON.stringify(request)),
        anamneseId: this.anamneseId,
        patientId: this.patientId
      }
    });
  }
}
